import React from "react";
import { Link } from "react-router-dom";

const VERSION = "1.0";
const VIGENTE_DESDE = "23 de mayo de 2026";
const CONTACTO_EMAIL = "contacto@procure.justsapiens.com";
const LEGAL_STATUS = "borrador_piloto"; // "borrador_piloto" | "final_revisado"

function LegalDraftBanner() {
  if (LEGAL_STATUS === "final_revisado") return null;
  return (
    <div
      className="mb-6 p-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-sm"
      data-testid="legal-draft-banner"
    >
      <p className="text-sm">
        <strong>Documento de borrador — versión piloto.</strong> Este texto refleja la mejor práctica
        del equipo operativo y está pendiente de revisión final por abogado titulado antes del lanzamiento
        comercial extendido. Si encuentras inconsistencias, escríbenos a{" "}
        <a href={`mailto:${CONTACTO_EMAIL}`} className="underline">{CONTACTO_EMAIL}</a>.
      </p>
    </div>
  );
}

export function TermsPage() {
  return (
    <div data-testid="terms-page" className="container-prose py-16 max-w-3xl">
      <Link to="/" className="text-sm text-gray-500 hover:text-primary">← Volver</Link>
      <p className="text-xs uppercase tracking-[0.22em] text-gray-500 mt-6 mb-2">
        Versión {VERSION} · Vigente desde {VIGENTE_DESDE}
      </p>
      <h1 className="font-serif text-4xl text-gray-900 mb-8">Términos del servicio</h1>

      <LegalDraftBanner />

      <div className="prose prose-sm text-gray-700 space-y-5 leading-relaxed">
        <h2 className="font-serif text-2xl text-gray-900 mt-8">1. Naturaleza del servicio</h2>
        <p>
          JustSapiens Procure (en adelante, la "Plataforma") es operada por JustSapiens y ofrece
          tres niveles de servicio: <strong>Explorador</strong> (prevalidación gratuita),
          <strong> Diagnóstico</strong> (revisión precontractual con análisis humano experto, COP 350.000)
          y <strong>Kit Documental</strong> (paquete documental inicial, COP 1.200.000).
        </p>
        <p>
          Los entregables de la Plataforma constituyen <strong>insumo de apoyo</strong> para procesos
          de contratación pública en Colombia. <strong>No sustituyen asesoría legal formal</strong>,
          dictamen del ordenador del gasto, concepto del comité asesor de contratación ni cualquier
          decisión final que corresponda a la entidad contratante. Cualquier uso o adopción de los
          contenidos generados es responsabilidad de quien los aplica.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">2. Limitaciones de responsabilidad</h2>
        <p>
          JustSapiens no garantiza resultados específicos en procesos de contratación que utilicen
          los insumos producidos por la Plataforma. JustSapiens no responde por decisiones tomadas
          por entidades contratantes, comités asesores, ordenadores del gasto, oferentes o terceros
          en función de los contenidos generados.
        </p>
        <p>
          La Plataforma usa modelos de inteligencia artificial cuyo output es revisado por personal
          humano antes de su entrega final. Aun así, pueden existir omisiones o imprecisiones; el
          usuario reconoce esta limitación y se compromete a contrastar la información con fuentes
          oficiales antes de cualquier decisión.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">3. Política de pagos</h2>
        <p>
          Los pagos de Diagnóstico y Kit Documental se realizan por canales <strong>manuales</strong>
          (transferencia bancaria, link de pago, QR Bre-B o coordinación por correo). El pago se
          confirma manualmente por el equipo operativo dentro de las 24 horas hábiles siguientes.
        </p>
        <p>
          <strong>Reembolsos</strong>: si el Diagnóstico no se ha generado, el reembolso procede
          completo dentro de 5 días hábiles. Si la generación inició pero no fue aprobada por el
          revisor, procede reembolso parcial del 70%. Una vez aprobado y entregado al usuario, no
          procede reembolso.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">4. Tiempos esperados de entrega</h2>
        <p>
          El Explorador es inmediato (≤ 2 minutos tras envío del caso). El Diagnóstico tiene un
          tiempo objetivo de <strong>24 a 72 horas hábiles</strong> tras confirmación de pago,
          dependiendo de la complejidad y la cola de revisión humana. El Kit Documental se cotiza y
          coordina caso a caso.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">5. Propiedad intelectual del output</h2>
        <p>
          El usuario recibe licencia de uso, reproducción y adaptación de los entregables para los
          fines de su propio proceso de contratación. JustSapiens conserva propiedad sobre la
          plataforma, los prompts, los modelos derivados y los algoritmos. El usuario se compromete
          a no redistribuir comercialmente los entregables sin autorización escrita.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">6. Derecho a eliminar cuenta y datos</h2>
        <p>
          El usuario puede solicitar en cualquier momento la eliminación de su cuenta y de los datos
          personales asociados, escribiendo a <a href={`mailto:${CONTACTO_EMAIL}`} className="text-primary underline">{CONTACTO_EMAIL}</a>.
          JustSapiens conserva los registros mínimos exigidos por ley (audit log financiero) según
          se detalla en la <Link to="/politica-datos" className="text-primary underline">Política de Tratamiento de Datos</Link>.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">7. Modificaciones</h2>
        <p>
          JustSapiens podrá modificar estos términos. Toda modificación entrará en vigencia a partir
          de su publicación. Cambios sustanciales se notificarán por correo electrónico al usuario
          registrado al menos 15 días antes de su entrada en vigor.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">8. Contacto y ley aplicable</h2>
        <p>
          Para consultas, peticiones, quejas o reclamos relacionados con el servicio, escribe a
          <a href={`mailto:${CONTACTO_EMAIL}`} className="text-primary underline"> {CONTACTO_EMAIL}</a>.
          Estos términos se rigen por la legislación de la República de Colombia.
        </p>
      </div>
    </div>
  );
}

export function DataPolicyPage() {
  return (
    <div data-testid="data-policy-page" className="container-prose py-16 max-w-3xl">
      <Link to="/" className="text-sm text-gray-500 hover:text-primary">← Volver</Link>
      <p className="text-xs uppercase tracking-[0.22em] text-gray-500 mt-6 mb-2">
        Versión {VERSION} · Vigente desde {VIGENTE_DESDE}
      </p>
      <h1 className="font-serif text-4xl text-gray-900 mb-8">
        Política de Tratamiento de Datos (Ley 1581 de 2012)
      </h1>

      <LegalDraftBanner />

      <div className="prose prose-sm text-gray-700 space-y-5 leading-relaxed">
        <p>
          Conforme a la <strong>Ley 1581 de 2012</strong>, el Decreto 1377 de 2013 y demás normas
          concordantes, JustSapiens —en calidad de Responsable del Tratamiento— informa a los
          titulares de datos personales las condiciones bajo las cuales se recolectan, almacenan,
          usan, transmiten y suprimen sus datos en JustSapiens Procure.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">1. Datos recolectados</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li><strong>Identificación</strong>: nombre, correo electrónico, rol institucional declarado.</li>
          <li><strong>Contenido del caso</strong>: objeto contractual, valor estimado, modalidad propuesta, intake completo del Explorador.</li>
          <li><strong>Pagos</strong>: referencia, monto, comprobante (si lo subes para conciliación manual). NO almacenamos números completos de tarjeta ni credenciales bancarias.</li>
          <li><strong>Uso de la plataforma</strong>: IP (hash, anonimizada), fecha y hora de cada acción registrada en audit log para integridad y trazabilidad.</li>
        </ul>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">2. Finalidades del tratamiento</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li>Prestar los servicios contratados (Explorador, Diagnóstico, Kit Documental).</li>
          <li>Generar entregables documentales y notificar al usuario su estado.</li>
          <li>Atender solicitudes de soporte, peticiones, quejas y reclamos.</li>
          <li>Mejorar la calidad del producto mediante análisis agregado y anonimizado.</li>
          <li>Cumplir obligaciones legales (auditoría financiera, requerimientos de autoridades).</li>
        </ul>
        <p>
          <strong>No se utilizan los datos del usuario para entrenar modelos de IA</strong> sin su
          autorización expresa y previa. El contenido enviado a proveedores LLM (OpenAI, Anthropic)
          se procesa bajo acuerdos comerciales con cláusula de no entrenamiento.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">3. Conservación de los datos</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li><strong>Cuenta activa</strong>: mientras el usuario mantenga su cuenta.</li>
          <li><strong>Casos y entregables</strong>: 5 años después del cierre del caso (período razonable para auditorías y disputas contractuales).</li>
          <li><strong>Audit log financiero</strong>: 10 años (régimen comercial colombiano).</li>
          <li><strong>Comprobantes de pago</strong>: 10 años (régimen tributario).</li>
        </ul>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">4. Derechos del titular</h2>
        <p>El titular tiene derecho a:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Conocer, actualizar y rectificar sus datos personales.</li>
          <li>Solicitar prueba de la autorización otorgada.</li>
          <li>Ser informado sobre el uso dado a sus datos.</li>
          <li>Presentar quejas ante la Superintendencia de Industria y Comercio.</li>
          <li>Revocar la autorización y/o solicitar la supresión cuando no exista obligación legal de conservar.</li>
          <li>Acceder gratuitamente a sus datos personales objeto de tratamiento.</li>
        </ul>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">5. Encargado de tratamiento y contacto</h2>
        <p>
          <strong>Responsable</strong>: JustSapiens.<br />
          <strong>Correo</strong>: <a href={`mailto:${CONTACTO_EMAIL}`} className="text-primary underline">{CONTACTO_EMAIL}</a><br />
          <strong>Tiempo máximo de respuesta</strong>: 15 días hábiles para consultas y 10 días hábiles para reclamos, conforme a Ley 1581 de 2012.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">6. Mecanismo para ejercer derechos</h2>
        <p>
          Para ejercer cualquiera de los derechos enumerados, envía un correo a <a href={`mailto:${CONTACTO_EMAIL}`} className="text-primary underline">{CONTACTO_EMAIL}</a> con:
        </p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Asunto: "Derecho de habeas data — [Consulta / Reclamo / Supresión]".</li>
          <li>Identificación del titular (nombre + email registrado).</li>
          <li>Descripción clara de los hechos y la solicitud.</li>
          <li>Datos de contacto para respuesta.</li>
        </ul>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">7. Transferencia internacional</h2>
        <p>
          Los servicios de hosting (MongoDB Atlas), procesamiento LLM (OpenAI, Anthropic, Google) y
          entrega de email transaccional (Resend) pueden implicar transferencia de datos a Estados
          Unidos y la Unión Europea. Estas transferencias se realizan bajo Cláusulas Contractuales
          Tipo o acuerdos equivalentes que garantizan un nivel de protección adecuado.
        </p>

        <h2 className="font-serif text-2xl text-gray-900 mt-8">8. Vigencia y modificaciones</h2>
        <p>
          Esta política se encuentra vigente desde el {VIGENTE_DESDE}. Cualquier cambio se publicará
          en esta página y, si es sustancial, se notificará al usuario por correo con al menos 15
          días de anticipación.
        </p>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div data-testid="not-found-page" className="container-prose py-24 text-center">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Error 404</p>
      <h1 className="font-serif text-5xl text-gray-900 mb-3">Página no encontrada</h1>
      <p className="text-gray-600 mb-6">La ruta a la que intentas acceder no existe.</p>
      <Link
        to="/"
        className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors text-sm"
      >
        Volver al inicio
      </Link>
    </div>
  );
}
